import {action, computed, observable} from "mobx";
import {
    apiDayOffAskAsAdminPost,
    apiDayOffDayOffApprovePut,
    apiDayOffDayOffDeclinePut,
    apiDayOffDayOffPut,
    apiDaysOffUserGet,
    DayOffForUserOutput,
    DayOffOutput,
    DayOffStatusEnum,
    DayOffUpdateInput
} from "../../../generated";
import moment, {Moment} from "moment";
import {v4} from 'uuid';
import {assert} from "ts-essentials";
import {handleFormSubmit} from "../../../utils/form/handleFormSubmit";
import UiBus from "../../../store/service/UiBus";

export const DAY_OFF_CALENDAR_FORMAT = 'YYYY-MM-DD';
export const TOTAL_DAY_OFF_PER_MEMBER = 20;
const YEAR_FORMAT = 'YYYY';

export class DayOffUserStore {
    @observable isLoading = false;
    @observable daysOffForUser?: DayOffForUserOutput;
    @observable selectedDayFormatted?: string;
    @observable selectedDayOff?: DayOffOutput;
    @observable isDayOffStatusChanging = false
    @observable currentYear = moment().format(YEAR_FORMAT);

    constructor(private uiBus: UiBus) {}


    @action onPreviousMonth() {
        this.currentYear = String(Number(this.currentYear) - 1);
    }

    @action onNextMonth() {
        this.currentYear = String(Number(this.currentYear) + 1);
    }

    @action loadUserDaysOff(userId: string) {
        this.isLoading = true;

        apiDaysOffUserGet(userId)
            .then(action((response) => {
                this.daysOffForUser = response;
            }))
            .finally(action(() => {
                this.isLoading = false;
            }))
            .catch(action(() => {

            }))
    }

    @computed get pendingDaysOff() {
        return this.daysOffForUser?.daysOff.filter(dayOff => dayOff.status === DayOffStatusEnum.Pending) || [];
    }

    @computed get approvedDaysOff() {
        return this.daysOffForUser?.daysOff.filter(dayOff => dayOff.status === DayOffStatusEnum.Approved) || [];
    }

    @computed get approvedDaysOffWithingCurrentYear() {
        return this.approvedDaysOff.filter(dayOff => {
            return moment(dayOff.date).format(YEAR_FORMAT) === this.currentYear;
        })
    }

    @computed get pendingDaysOffWithingCurrentYear() {
        return this.pendingDaysOff.filter(dayOff => {
            return moment(dayOff.date).format(YEAR_FORMAT) === this.currentYear;
        })
    }

    @action openDayModal(day: Moment) {
        const formatted = day.format(DAY_OFF_CALENDAR_FORMAT);

        const foundDayOff = this.dayOffsCached[formatted];

        if (foundDayOff) {
            this.selectedDayOff = foundDayOff;
        }

        this.selectedDayFormatted = formatted;
    }

    get isDayOffModalOpen() {
        return this.selectedDayFormatted;
    }

    get dayOffsCached() {
        const object: Record<string, DayOffOutput> = {};

        this.approvedDaysOff
            .forEach(dayOff => {
                object[moment(dayOff.date).format(DAY_OFF_CALENDAR_FORMAT)] = dayOff;
            })

        return object;
    }

    @action closeModal() {
        this.selectedDayFormatted = undefined;
        this.selectedDayOff = undefined;
    }

    @action async submit(values: DayOffUpdateInput) {
        assert(this.selectedDayFormatted);
        assert(this.daysOffForUser);
        if (this.selectedDayOff) {
            const response = await handleFormSubmit(apiDayOffDayOffPut(this.selectedDayOff.id, {
                reason: values.reason,
                date: null,
                forUser: null,
            }))
            if (response.errors) {
                this.uiBus.notify("Something went wrong", "warning");
                return response.errors;
            }
        } else {
            const response = await handleFormSubmit(apiDayOffAskAsAdminPost({
                id: v4(),
                date: this.selectedDayFormatted,
                forUser: `/api/users/${this.daysOffForUser.forUser.id}`,
                reason: values.reason,
            }))
            if (response.errors) {
                this.uiBus.notify("Something went wrong", "warning");
                return response.errors;
            }
        }

        this.loadUserDaysOff(this.daysOffForUser.forUser.id.toString());
        this.closeModal();
    }

    @action changeDayOffStatus(dayOff: DayOffOutput, status: DayOffStatusEnum.Approved | DayOffStatusEnum.Declined) {
        this.isDayOffStatusChanging = true;

        // these 2 endpoints have the same type signature, so it's OK to have dynamic endpoint here
        const endpoint = status === DayOffStatusEnum.Approved ? apiDayOffDayOffApprovePut : apiDayOffDayOffDeclinePut;
        const successMessage = status === DayOffStatusEnum.Approved ? 'The day off has been approved' : 'The day off has been declined';

        endpoint(dayOff.id)
            .then(() => {
                assert(this.daysOffForUser);
                this.loadUserDaysOff(this.daysOffForUser.forUser.id.toString());
                this.uiBus.notify(successMessage, "success");
            })
            .catch(() => {
                this.uiBus.notify("Something went wrong", "warning");
            })
            .finally(action(() => {
                this.isDayOffStatusChanging = false;
            }));
    }
}